import React from 'react';
import CheckRow from '../qc/CheckRow';
import CHECK_OPTIONS from '../qc/checkOptions';

function TestChecklist({
  st,
  curDev, bDevs, bIdx, setBIdx,
  bRes, setBRes, bNotes, setBNotes,
  bTester, batchHomeLoc, bMode, bFwVersion,
  bSaving, bIsComplete, bStartTime,
  lbRtkTiming, getDefs, getPF,
  DM, OW, darkMode, IC, ss, bt,
  saveBatch, setBChk,
  bDevSearch, setBDevSearch,
  applyScanner,
  fmtTime, fmtDur,
  onSaveForLater,
  bManualSelRef,
  requireRtkFix,
}) {
  if (!curDev) return null;

  const defs = getDefs(curDev.type);
  const pfDefs = getPF(curDev.type);

  // Compute device status from current bRes
  const anyFail = pfDefs.some(t => bRes[curDev.id]?.[t.id] === 'fail');


  // Device header metadata
  const dispatchedDate = curDev.locationLog?.[0]?.date
    ? new Date(curDev.locationLog[0].date).toLocaleDateString('en-GB')
    : 'N/A';
  const lastResult = (st.testResults || [])
    .filter(r => r.deviceId === curDev.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  const lastChecked = lastResult
    ? new Date(lastResult.date).toLocaleDateString('en-GB') + ' · ' + new Date(lastResult.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;

  // RTK timing for current device
  const lbId = curDev.deviceId ? String(curDev.deviceId) : null;
  const lbT = lbId ? lbRtkTiming[lbId] : null;
  const noId = !lbId;
  const powerOnStr = lbT?.powerOnAt ? fmtTime(lbT.powerOnAt) : '—';
  const rtkFixStr = lbT?.rtkFixAt ? fmtTime(lbT.rtkFixAt) : lbT?.loading ? '…' : '—';
  const ttfStr = lbT?.ttfMs != null ? `${Math.round(lbT.ttfMs / 1000)}s` : '—';
  const hasfix = !!lbT?.rtkFixAt;
  const refreshAgoSec = lbT?.lastPolledAt ? Math.floor((Date.now() - lbT.lastPolledAt) / 1000) : null;
  const refreshStr = lbT?.loading ? 'Refreshing…' : refreshAgoSec == null ? '—' : refreshAgoSec < 5 ? 'Just now' : `${refreshAgoSec}s ago`;

  // For notes: show in first failing check's expanded section
  const firstFailDefId = pfDefs.find(t => bRes[curDev.id]?.[t.id] === 'fail')?.id;

  // RTK duration for rtkFix check
  const rtkDur = lbT?.ttfMs != null ? `${Math.round(lbT.ttfMs / 1000)}s` : null;

  // Save Session button state
  const allComplete = bDevs.every(d => bIsComplete(d));
  const allChecksAnswered = bDevs.every(d => getPF(d.type).every(t => bRes[d.id]?.[t.id] === 'pass' || bRes[d.id]?.[t.id] === 'fail' || bRes[d.id]?.[t.id] === 'na'));
  const rtkPending = requireRtkFix && allChecksAnswered && bDevs.some(d => {
    if (!d.deviceId) return false;
    const hasFail = getPF(d.type).some(t => bRes[d.id]?.[t.id] === 'fail');
    return !hasFail && !lbRtkTiming[String(d.deviceId)]?.rtkFixAt;
  });
  const saveDisabled = bSaving || !allComplete || !bTester.trim();
  let saveLabel = 'Save Session';
  if (bSaving) saveLabel = 'Saving…';
  else if (!bTester.trim()) saveLabel = 'Enter tester name to save';
  else if (rtkPending) saveLabel = 'Waiting for RTK fix…';
  else if (!allComplete) saveLabel = 'Complete all to save';

  return (
    <div>

      {/* Device header card */}
      <div className="device-header-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', letterSpacing: '-0.5px' }}>{curDev.serial}</span>
              <span className="pill pill-neutral" style={{ fontSize: 11, fontWeight: 700 }}>{curDev.type}</span>
              {bMode === 'firmware' && bFwVersion && (
                <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, background: 'rgba(98,0,234,0.15)', border: '1px solid rgba(98,0,234,0.3)', color: '#c4b5fd', fontWeight: 700, fontFamily: 'monospace' }}>FW TEST: v{bFwVersion}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: 11, color: 'var(--text-muted)' }}>
              <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, fontSize: 10 }}>
                BATCH <span style={{ fontWeight: 400, letterSpacing: 0 }}>{bMode === 'firmware' ? 'FW Test' : 'Batch 1'}</span>
              </span>
              <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, fontSize: 10 }}>
                DISPATCHED <span style={{ fontWeight: 400, letterSpacing: 0 }}>{dispatchedDate}</span>
              </span>
              {lastChecked && (
                <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, fontSize: 10 }}>
                  LAST CHECKED <span style={{ fontWeight: 400, letterSpacing: 0 }}>{lastChecked}</span>
                </span>
              )}
              {bTester && (
                <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, fontSize: 10 }}>
                  CHECKED BY <span style={{ fontWeight: 400, letterSpacing: 0 }}>{bTester}</span>
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
            {anyFail && (
              <span style={{ fontSize: 12, color: 'var(--danger-text)', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>Raise RMA ↗</span>
            )}
          </div>
        </div>
      </div>

      {/* Device navigation bar */}
      <div style={{ marginBottom: 8, textAlign: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
          Device {bIdx + 1} of {bDevs.length}{batchHomeLoc ? ' · ' + batchHomeLoc : ''}
        </span>
      </div>

      {/* Device strip */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, overflowX: 'auto', paddingBottom: 4 }}>
        {bDevs.map((d, i) => {
          const checksAnswered = getPF(d.type).every(t => bRes[d.id]?.[t.id] === 'pass' || bRes[d.id]?.[t.id] === 'fail' || bRes[d.id]?.[t.id] === 'na');
          const hasFail = getPF(d.type).some(t => bRes[d.id]?.[t.id] === 'fail');
          const rtkBlocked = !!(requireRtkFix && d.deviceId && checksAnswered && !hasFail && !lbRtkTiming[String(d.deviceId)]?.rtkFixAt);
          const isActive = i === bIdx;
          const isDone = bIsComplete(d);

          let btnClass = 'btn btn-ghost';
          let extraStyle = { fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: isActive ? 600 : 400 };

          if (isActive) {
            extraStyle.border = '2px solid var(--brand-default)';
            extraStyle.color = 'var(--brand-default)';
          } else if (isDone && !hasFail) {
            extraStyle.background = 'var(--success-fill)';
            extraStyle.color = 'var(--success-text)';
            extraStyle.border = '1px solid var(--success-border)';
          } else if (hasFail && checksAnswered) {
            extraStyle.background = 'var(--danger-fill)';
            extraStyle.color = 'var(--danger-text)';
            extraStyle.border = '1px solid var(--danger-border)';
          } else if (rtkBlocked) {
            extraStyle.background = 'var(--warning-fill)';
            extraStyle.color = 'var(--warning-text)';
            extraStyle.border = '1px solid var(--warning-border)';
          }

          return (
            <button key={d.id} onClick={() => setBIdx(i)} className={btnClass} style={extraStyle}>
              {isDone && !hasFail ? '✓ ' : rtkBlocked ? '⚠ ' : ''}{d.serial}
            </button>
          );
        })}
      </div>

      {/* Jump search */}
      {(() => {
        const bq = bDevSearch.toLowerCase();
        const matches = bq ? bDevs.map((d, i) => ({ d, i })).filter(({ d }) => (d.serial || '').toLowerCase().includes(bq) || (d.type || '').toLowerCase().includes(bq)) : [];
        return (
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input value={bDevSearch} onChange={e => setBDevSearch(applyScanner(bDevSearch, e.target.value, 'replace'))} placeholder="Jump to device — search serial or type..." style={{ ...ss, fontSize: 13, paddingLeft: 32 }} />
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            {matches.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 99, background: 'var(--surface-overlay)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', boxShadow: '0 4px 16px rgba(0,0,0,0.25)', maxHeight: 220, overflowY: 'auto', marginTop: 3 }}>
                {matches.map(({ d, i }) => {
                  const isDone = bIsComplete(d);
                  return (
                    <div key={d.id} onClick={() => { bManualSelRef.current = true; setBIdx(i); setBDevSearch(''); }} className="list-row" style={{ borderRadius: 0, border: 'none', borderBottom: '1px solid var(--border-subtle)', background: i === bIdx ? 'var(--brand-tint)' : undefined }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        {isDone
                          ? <span style={{ color: 'var(--success-text)', fontSize: 13 }}>✓</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{i + 1}</span>}
                        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-default)' }}>{d.serial}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.type}</span>
                      </div>
                      <span className={isDone ? 'pill pill-success' : 'pill pill-neutral'} style={{ fontSize: 11 }}>{isDone ? 'Done' : 'Pending'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* RTK timing strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 12, borderRadius: 'var(--radius-xl)', overflow: 'hidden', border: '1px solid var(--border-default)', opacity: noId ? 0.45 : 1 }}>
        {[
          { label: 'Power On', value: noId ? 'No Device ID' : powerOnStr, colorClass: null },
          { label: 'RTK Fix', value: noId ? '—' : rtkFixStr, colorClass: hasfix ? 'success' : null },
          { label: 'Time to Fix', value: noId ? '—' : ttfStr, colorClass: hasfix ? 'success' : null },
          { label: 'API Refresh', value: noId ? '—' : refreshStr, colorClass: lbT?.loading ? 'warning' : null },
        ].map((item, i, arr) => (
          <div key={item.label} style={{ padding: '8px 12px', background: 'var(--surface-inset)', borderRight: i < arr.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
            <div style={{ color: 'var(--text-subtle)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{item.label}</div>
            <div style={{
              fontFamily: 'monospace',
              fontWeight: 600,
              fontSize: 12,
              color: item.colorClass === 'success' ? 'var(--success-text)' : item.colorClass === 'warning' ? 'var(--warning-text)' : 'var(--text-default)',
            }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Session start timer */}
      <div className="card" style={{ padding: '10px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, borderColor: bStartTime ? 'var(--brand-default)' : undefined }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={bStartTime ? 'var(--brand-default)' : 'var(--text-subtle)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
        <div>
          <div style={{ fontSize: 10, color: bStartTime ? 'var(--brand-default)' : 'var(--text-subtle)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session Start</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: bStartTime ? 'var(--brand-default)' : 'var(--text-subtle)', fontFamily: 'monospace' }}>{bStartTime ? fmtTime(bStartTime) : 'Waiting for first device power-on…'}</div>
        </div>
      </div>

      {/* Checklist */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 10 }}>
        {defs.map((t, i) => {
          const v = bRes[curDev.id]?.[t.id];
          const isFail = v === 'fail';
          const isFirstFail = isFail && t.id === firstFailDefId;

          return (
            <CheckRow
              key={t.id}
              def={t}
              value={v}
              index={i}
              total={defs.length}
              minHeight={52}
              options={CHECK_OPTIONS}
              onAnswer={val => {
                setBChk(curDev.id, t.id, val);
                // Bench-test rule: a fail ends the walk for this unit — it is
                // going to quarantine regardless, so the untouched checks are
                // recorded N/A rather than left open. Goods-in QC deliberately
                // does the opposite; CheckRow itself stays neutral on this.
                if (val === 'fail') {
                  const idx = pfDefs.findIndex(x => x.id === t.id);
                  const remaining = pfDefs.slice(idx + 1).filter(x => !bRes[curDev.id]?.[x.id] || bRes[curDev.id]?.[x.id] === '');
                  if (remaining.length > 0) {
                    setBRes(r => ({ ...r, [curDev.id]: { ...r[curDev.id], ...Object.fromEntries(remaining.map(x => [x.id, 'na'])) } }));
                  }
                }
              }}
              onClear={() => setBChk(curDev.id, t.id, '')}
              footer={isFail ? (
                <div className="check-fail-body">
                  <div className="check-meta-row">
                    {bTester && <span>CHECKED BY <strong>{bTester}</strong></span>}
                    {t.id === 'rtkFix' && rtkDur && <span>DURATION <strong>{rtkDur}</strong></span>}
                  </div>
                  {isFirstFail && (
                    <div className="failure-notes-block">
                      <div className="failure-notes-label">FAILURE NOTES</div>
                      <textarea
                        value={bNotes[curDev.id] || ''}
                        onChange={e => { const val = e.target.value; setBNotes(n => ({ ...n, [curDev.id]: val })); }}
                        placeholder="Describe what went wrong..."
                        rows={3}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--warning-border)', background: 'var(--warning-fill)', color: 'var(--text-default)', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
                      />
                    </div>
                  )}
                </div>
              ) : null}
            />
          );
        })}

        {/* Notes section when no failures (general observations) */}
        {!anyFail && (
          <div style={{ padding: '12px 16px' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-subtle)', marginBottom: 3, display: 'block' }}>Notes</label>
            <textarea
              value={bNotes[curDev.id] || ''}
              onChange={e => { const val = e.target.value; setBNotes(n => ({ ...n, [curDev.id]: val })); }}
              placeholder="Observations..."
              rows={2}
              style={{ ...ss, fontSize: 12, resize: 'vertical' }}
            />
          </div>
        )}
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, paddingBottom: 24, alignItems: 'center' }}>
        {bIdx > 0 && (
          <button onClick={() => setBIdx(i => i - 1)} className="btn btn-ghost" style={{ fontSize: 13 }}>← Prev</button>
        )}
        <button onClick={onSaveForLater} className="btn btn-ghost" style={{ fontSize: 13 }}>Save for later</button>
        <div style={{ flex: 1 }} />
        {bIdx < bDevs.length - 1
          ? <button onClick={() => setBIdx(i => i + 1)} className="btn btn-primary" style={{ fontSize: 13, padding: '8px 20px' }}>Next →</button>
          : <button
              onClick={saveBatch}
              disabled={saveDisabled}
              className="btn btn-primary"
              style={{
                fontSize: 13,
                padding: '8px 20px',
                opacity: saveDisabled ? 0.5 : 1,
                cursor: saveDisabled ? 'not-allowed' : 'pointer',
                background: !saveDisabled ? 'var(--success-text)' : undefined,
              }}
            >
              {saveLabel}
            </button>
        }
      </div>
    </div>
  );
}

export default TestChecklist;
